import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { decodeVersion, SceneStore } from '@os/doc';
import { useApi } from '../../app/providers';
import { ApiError } from '../../lib/api';
import type { BoardVersion } from '../../lib/version-types';
import { Button } from '../../ui';
import { ConfirmDialog } from '../../ui/ConfirmDialog';
import { Editor } from '../editor/Editor';
import { BoardMedia } from '../media/board-media';
import { LocalMediaStore, memoryBackend } from '../media/store';
import { versionTitle } from './labels';
import { versionKeys } from './useVersions';
import { BoardSkeleton } from '../../ui/states';

type Props = { boardId: string; version: BoardVersion; canRestore: boolean; onClose: () => void; onRestored: () => void };
type State = { status: 'loading' } | { status: 'error' } | { status: 'ready'; store: SceneStore; media: BoardMedia };

/**
 * Read-only preview of a version (spec §11.1): the file is decoded into a separate document shown by the same editor,
 * over the live board. Images resolve through `asset_urls`, which also knows the assets of the board's versions.
 */
export function VersionPreview({ boardId, version, canRestore, onClose, onRestored }: Props) {
  const { t, i18n } = useTranslation();
  const api = useApi();
  const queryClient = useQueryClient();
  const [state, setState] = useState<State>({ status: 'loading' });
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    let alive = true;
    let opened: { media: BoardMedia; local: LocalMediaStore } | null = null;
    setState({ status: 'loading' });
    (async () => {
      const doc = await decodeVersion(await api.fetchVersion(boardId, version.id));
      const store = new SceneStore(doc);
      store.setReadOnly(true);
      const local = await LocalMediaStore.open(memoryBackend());
      const media = new BoardMedia({ boardId, api, local });
      opened = { media, local };
      if (!alive) return;
      setState({ status: 'ready', store, media });
    })().catch(() => alive && setState({ status: 'error' }));
    return () => {
      alive = false;
      opened?.media.destroy();
      opened?.local.close();
    };
  }, [api, boardId, version.id]);

  const restore = useMutation({
    mutationFn: () => api.restoreVersion(boardId, version.id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: versionKeys.list(boardId) });
      onRestored();
    },
  });
  const restoreError = restore.error
    ? restore.error instanceof ApiError && restore.error.code === 'conflict'
      ? t('history.busy')
      : t('history.restoreError')
    : null;
  const when = new Intl.DateTimeFormat(i18n.language, { dateStyle: 'medium', timeStyle: 'short' }).format(new Date(version.createdAt));

  return (
    <div
      role="region"
      aria-label={t('history.previewLabel')}
      onKeyDown={(e) => {
        // The confirmation is portaled out of the region but React still bubbles its keys here: its Escape is its own.
        if (e.key !== 'Escape' || !e.currentTarget.contains(e.target as Node)) return;
        e.stopPropagation();
        onClose();
      }}
      className="fixed inset-0 z-40 flex flex-col bg-bg"
    >
      <div className="flex flex-wrap items-center gap-3 border-b border-border bg-chrome px-4 py-2 shadow-float">
        <p className="font-sans text-sm text-fg">{t('history.previewOf', { title: versionTitle(version, t), date: when })}</p>
        <div className="ml-auto flex flex-wrap items-center gap-2">
          {canRestore && (
            <Button variant="primary" disabled={state.status !== 'ready' || restore.isPending} onClick={() => setConfirming(true)}>
              {t('history.restore')}
            </Button>
          )}
          <Button onClick={onClose}>
            {t('history.back')}
          </Button>
        </div>
        {restoreError && (
          <p role="alert" className="w-full font-sans text-xs text-danger">
            {restoreError}
          </p>
        )}
      </div>
      <div className="relative flex-1">
        {state.status === 'ready' ? (
          // Its pages are the version's: the board's `?page=` and cameras stay as they are (P14).
          <Editor store={state.store} media={state.media} pageUrl={null} />
        ) : state.status === 'error' ? (
          <p role="alert" className="flex h-full items-center justify-center font-sans text-fg-muted">
            {t('history.previewError')}
          </p>
        ) : (
          <BoardSkeleton label={t('board.loading')} />
        )}
      </div>
      <ConfirmDialog
        open={confirming}
        title={t('p18.e.restoreTitle')}
        body={t('history.restoreWarning')}
        confirmLabel={t('history.confirmRestore')}
        cancelLabel={t('p18.e.cancel')}
        onConfirm={() => {
          setConfirming(false);
          restore.mutate();
        }}
        onCancel={() => setConfirming(false)}
      />
    </div>
  );
}
